import React, { useState } from 'react';
import { X, Crown, Clock, CheckCircle, Gift, Star } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { SUBSCRIPTION_PLANS } from '../../constants/subscriptionPlans';
import { SUBSCRIPTION_PLAN_IDS } from '../../types/subscription';
import { subscriptionService } from '../../services/subscriptionService';

interface FreeTrialModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onError: (error: string) => void;
  onSkip?: () => void;
}

const FreeTrialModal: React.FC<FreeTrialModalProps> = ({
  isOpen,
  onAccept,
  onError,
  onSkip
}) => {
  const { language, isRTL, strings } = useLocalization();
  const { user } = useAuth();
  const { state: subscriptionState } = useSubscription();
  const [loading, setLoading] = useState(false);

  const professionalPlan = SUBSCRIPTION_PLANS.find(plan => plan.id === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL);

  if (!isOpen || !professionalPlan) {
    return null;
  }

  // Get trial end date from subscription
  const getTrialEndDate = (): Date | null => {
    if (subscriptionState.currentSubscription?.endDate) {
      return new Date(parseInt(subscriptionState.currentSubscription.endDate));
    }
    return null;
  };

  // Format date for display
  const formatTrialEndDate = (): string => {
    const endDate = getTrialEndDate();
    if (!endDate) {
      // Fallback: Calculate 14 days from now if subscription not loaded yet
      // This should rarely happen as modal is only shown after subscription is loaded
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 14);
      defaultDate.setHours(23, 59, 59, 999);
      
      // Format the fallback date
      if (language === 'ar') {
        const day = defaultDate.getDate();
        const monthNames = [
          'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
          'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
        ];
        const month = monthNames[defaultDate.getMonth()];
        const year = defaultDate.getFullYear();
        return `${day} ${month} ${year} الساعة 11:59 مساءً`;
      } else {
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const month = monthNames[defaultDate.getMonth()];
        const day = defaultDate.getDate();
        const year = defaultDate.getFullYear();
        return `${month} ${day}, ${year} at 11:59 PM`;
      }
    }

    if (language === 'ar') {
      // Arabic format: "31 ديسمبر 2025 الساعة 11:59 مساءً"
      const day = endDate.getDate();
      const monthNames = [
        'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
        'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
      ];
      const month = monthNames[endDate.getMonth()];
      const year = endDate.getFullYear();
      const hours = endDate.getHours();
      const minutes = endDate.getMinutes();
      const ampm = hours >= 12 ? 'مساءً' : 'صباحاً';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${day} ${month} ${year} الساعة ${displayHours}:${displayMinutes} ${ampm}`;
    } else {
      // English format: "December 31, 2025 at 11:59 PM"
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const month = monthNames[endDate.getMonth()];
      const day = endDate.getDate();
      const year = endDate.getFullYear();
      const hours = endDate.getHours();
      const minutes = endDate.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${month} ${day}, ${year} at ${displayHours}:${displayMinutes} ${ampm}`;
    }
  };

  const handleAcceptTrial = async () => {
    if (!user?.id) {
      onError('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      // Import trial extension service dynamically to avoid circular imports
      const { trialExtensionService } = await import('../../services/trialExtensionService');
      
      // Get user registration date (use current date for existing users)
      const userRegistrationDate = new Date();
      
      // Create extended trial subscription using trial extension service
      const newSubscription = await trialExtensionService.createOrUpdateExtendedTrialSubscription(
        user.id,
        userRegistrationDate,
        user.name,
        user.phoneNumber
      );

      // Create initial usage stats
      // Note: Usage stats will be calculated locally from cache

      console.log('Extended trial activated:', newSubscription);
      onAccept();

    } catch (err) {
      console.error('Error activating extended trial:', err);
      onError(err instanceof Error ? err.message : 'Failed to activate extended trial');
    } finally {
      setLoading(false);
    }
  };


  const getFeatures = () => [
    {
      icon: <Crown className="w-5 h-5 text-accent" />,
      text: language === 'ar' ? 'مكاتب غير محدودة' : 'Unlimited Offices',
      description: language === 'ar' ? 'أضف عدد غير محدود من المكاتب' : 'Add unlimited offices'
    },
    {
      icon: <Star className="w-5 h-5 text-accent" />,
      text: language === 'ar' ? 'عملاء غير محدودين' : 'Unlimited Clients',
      description: language === 'ar' ? 'أضف عملاء غير محدودين لكل مكتب' : 'Add unlimited clients per office'
    },
    {
      icon: <Gift className="w-5 h-5 text-accent" />,
      text: language === 'ar' ? 'ملفات غير محدودة' : 'Unlimited Files',
      description: language === 'ar' ? 'أضف ملفات غير محدودة لكل عميل' : 'Add unlimited files per client'
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-accent" />,
      text: language === 'ar' ? 'جميع المميزات المتقدمة' : 'All Advanced Features',
      description: language === 'ar' ? 'إحصائيات، مجتمع، دعم هاتفي، تصدير إكسل' : 'Statistics, Community, Phone Support, Excel Export'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary to-accent text-white p-8 rounded-t-2xl">
          {/* Close Button */}
          {onSkip && (
            <button
              onClick={onSkip}
              className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          )}
          <div className="text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold mb-2">
              {language === 'ar' ? '🎉 مرحباً بك في منصة مرافعة!' : '🎉 Welcome to Morafaa!'}
            </h2>
            <p className="text-lg opacity-90">
              {language === 'ar' 
                ? `احصل على تجربة مجانية ممتدة حتى ${formatTrialEndDate()} من الخطة المهنية`
                : `Get an extended free trial until ${formatTrialEndDate()} of our Professional Plan`
              }
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Trial Offer */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-accent bg-opacity-10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Clock className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'تجربة مجانية' : 'Free Trial'}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'ar' ? 'جرب جميع المميزات مجاناً' : 'Try All Features for Free'}
            </h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-semibold text-lg">
                {language === 'ar' 
                  ? `⏰ فترة التجربة: حتى ${formatTrialEndDate()}`
                  : `⏰ Trial Period: Until ${formatTrialEndDate()}`
                }
              </p>
            </div>
            <p className="text-gray-600 text-lg">
              {language === 'ar' 
                ? 'لا توجد التزامات، يمكنك الإلغاء في أي وقت'
                : 'No commitments, cancel anytime'
              }
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {getFeatures().map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  {feature.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {feature.text}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Trial Benefits */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {language === 'ar' ? 'ما ستحصل عليه:' : 'What You\'ll Get:'}
            </h4>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                {language === 'ar' 
                  ? 'وصول كامل لجميع المميزات المتقدمة'
                  : 'Full access to all advanced features'
                }
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                {language === 'ar' 
                  ? 'دعم فني مخصص خلال فترة التجربة'
                  : 'Dedicated support during trial period'
                }
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                {language === 'ar' 
                  ? 'لا توجد رسوم خفية أو التزامات'
                  : 'No hidden fees or commitments'
                }
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                {language === 'ar' 
                  ? 'يمكنك الإلغاء في أي وقت'
                  : 'Cancel anytime you want'
                }
              </li>
            </ul>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={handleAcceptTrial}
              disabled={loading}
              className="w-full max-w-md bg-accent text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {language === 'ar' ? 'جاري التفعيل...' : 'Activating...'}
                </div>
              ) : (
                <div className="flex items-center">
                  <Crown className="w-5 h-5 mr-2" />
                  {language === 'ar' ? 'ابدأ التجربة الممتدة' : 'Start Extended Trial'}
                </div>
              )}
            </button>
          </div>

          {/* Skip Button */}
          {onSkip && (
            <div className="flex justify-center mt-4">
              <button
                onClick={onSkip}
                className="text-gray-500 hover:text-gray-700 text-sm underline"
              >
                {language === 'ar' ? 'تخطي الآن' : 'Skip for now'}
              </button>
            </div>
          )}

          {/* Footer Note */}
          <div className="text-center mt-6 text-sm text-gray-500">
            {language === 'ar' ? (
              <>
                بالضغط على "ابدأ التجربة المجانية" فإنك توافق على{' '}
                <a href="/terms" className="text-accent hover:underline">شروط الخدمة</a>
                {' '}و{' '}
                <a href="/privacy" className="text-accent hover:underline">سياسة الخصوصية</a>
              </>
            ) : (
              <>
                By clicking "Start Free Trial" you agree to our{' '}
                <a href="/terms" className="text-accent hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-accent hover:underline">Privacy Policy</a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTrialModal;
